import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Route, Navigate } from 'react-router-dom';
import { Translate } from 'react-redux-i18n';
import Spinner from '../../components/common/spinner.jsx';
import { grecaptcha } from '../../helpers/grecaptcha';
import { clear } from '../../modules/general';
import { refreshSession } from '../../modules/session';
import withMfaCheck from '../../components/withMfaCheck';
import ForcePasswordResetWall from '../../components/forcePasswordReset/ForcePasswordResetWall';
import { withRouterV6 } from '../../helpers/withRouterV6';

export class SecureRoute extends Component {
  refreshAttempted = false;

  componentDidUpdate() {
    if (
      !this.refreshAttempted &&
      !this.props.verification &&
      !this.props.session.processing &&
      this.props.session.error
    ) {
      const storageSession = localStorage.getItem('session');

      if (storageSession === 'undefined') {
        localStorage.removeItem('session');
      } else {
        const session = storageSession && JSON.parse(storageSession);
        if (session && session.refresh) {
          this.refreshAttempted = true;
          const tokenCallback = (token) =>
            this.props.refreshSession(
              session.account.id,
              session.refresh,
              token
            );
          grecaptcha(tokenCallback, 'SessionRefresh');
        }
      }
    }
  }

  render() {
    if (this.props.session.processing) {
      return <Spinner />;
    }

    // If MFA is pending (e.g. refresh returned mfaRequired), redirect to MFA page
    if (this.props.session.mfaPending && this.props.session.pendingSessionToken) {
      let mfaUrl = "/verify-mfa";
      const location = this.props.location;
      const returnUrl = `${location.pathname}${location.search}${location.hash}`;
      if (returnUrl !== "/") {
        mfaUrl = mfaUrl.concat(`?return=${encodeURIComponent(returnUrl)}`);
      }
      return <Navigate to={mfaUrl} replace />;
    }

    // If session key is null (logged out), redirect to login
    if (!this.props.session || this.props.session.key === null) {
      // If a refresh token exists and we're about to attempt a refresh,
      // show a loading state instead of flashing the login page
      if (this.props.session?.error && !this.props.verification) {
        const storageSession = localStorage.getItem('session');
        const parsed = storageSession && storageSession !== 'undefined' && JSON.parse(storageSession);
        if (parsed && parsed.refresh) {
          return <Spinner message={<Translate value="general.session-refreshing" />} />;
        }
      }

      let url = "/login";

      const location = this.props.location;
      const returnUrl = `${location.pathname}${location.search}${location.hash}`

      if (returnUrl !== "/") {
        url = url.concat(`?return=${encodeURIComponent(returnUrl)}`)
      }

      return <Navigate to={url} replace />;
    }

    // If authenticated, render children
    return this.props.children;
  }
}

const mapState = ({ session, general }) => ({
  session: session,
  verification: general.verification
});

const mapDispatch = (dispatch) => {
  return {
    clear() {
      dispatch(clear());
    },
    refreshSession(accountId, refreshCode, verificationToken = null) {
      dispatch(refreshSession(accountId, refreshCode, verificationToken));
    }
  };
};

const ConnectedSecureRoute = connect(mapState, mapDispatch)(SecureRoute);

/**
 * Password first, then MFA. The reset wall renders alongside the route rather than inside it, so
 * SecureRoute itself stays free of the store and can still be tested in isolation, which matters
 * because it is the gate every authenticated route passes through.
 *
 * withMfaCheck stands down while a rotation is outstanding, since the API refuses MFA setup until
 * the password is trusted, so the reset wall is the only one that can appear in that state.
 */
const SecureRouteWithPasswordResetWall = (props) => (
  <>
    <ForcePasswordResetWall />
    <ConnectedSecureRoute {...props} />
  </>
);

export default withRouterV6(withMfaCheck(SecureRouteWithPasswordResetWall));
